import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  Loader2, 
  Mail, 
  ArrowRight,
  CheckCircle,
  Smartphone,
  Sun,
  Moon
} from 'lucide-react';

// Premium stock images
const IMAGES = {
  loginBg: 'https://images.unsplash.com/photo-1764265992174-f483492edb77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxmaW50ZWNoJTIwc2VjdXJpdHklMjBzaGllbGQlMjBkaWdpdGFsJTIwYWJzdHJhY3QlMjBibHVlfGVufDB8fHx8MTc3MjE4MDEyNXww&ixlib=rb-4.1.0&q=85',
  officeModern: 'https://images.pexels.com/photos/5483051/pexels-photo-5483051.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
};

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const BrandName = () => {
    const name = branding.brand_name || 'AwadhPay';
    const m = /^(.*?)(pay)$/i.exec(name);
    return m ? <>{m[1]}<span className="text-primary">{m[2]}</span></> : <>{name}</>;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Merchant + reseller share this portal; admins use /admin/login
      const u = await login(formData.email, formData.password, ['merchant', 'reseller']);
      toast.success('Welcome back!');
      navigate(u.role === 'reseller' ? '/reseller' : '/dashboard');
    } catch (error) {
      // Check if this is a role restriction error
      if (error.isRoleError) {
        toast.error('Administrators must use the Admin Login portal.');
        setErrors({ submit: 'This login is for merchants only. Please use the Admin Login at /admin/login for administrator access.' });
      } else {
        const message = error.response?.data?.detail || 'Invalid email or password';
        toast.error(message);
        setErrors({ submit: message });
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, text: 'Bank-grade security' },
    { icon: Lock, text: '256-bit encryption' },
    { icon: CheckCircle, text: 'RBI compliant' },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Premium Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src={IMAGES.officeModern}
          alt="Modern Office"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/95 via-background/80 to-primary/20" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
              <CreditCard className="w-5 h-5 text-primary" />
            </div>
            <span className="text-2xl font-heading font-bold tracking-tight">
              <BrandName />
            </span>
          </Link>
          
          {/* Middle Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-heading font-bold leading-tight mb-4">
                Secure Access to Your
                <span className="text-gradient block">Payment Dashboard</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md">
                Monitor transactions, manage settlements, and track your business performance in real-time.
              </p>
            </div>
            
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-3 text-muted-foreground"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-4 h-4 text-primary" />
                  </div>
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <p className="text-sm text-muted-foreground">
            © 2026 {branding.brand_name}. {branding.description}
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="flex items-center justify-between p-6">
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-primary/20 flex items-center justify-center border border-primary/30">
              <CreditCard className="w-4 h-4 text-primary" />
            </div>
            <span className="text-xl font-heading font-bold">
              <BrandName />
            </span>
          </Link>
          <div className="flex items-center gap-3 ml-auto">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-sm">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <span className="text-sm text-muted-foreground hidden sm:inline">New here?</span>
            <Button variant="outline" onClick={() => navigate('/register')} className="rounded-sm" data-testid="goto-register-btn">
              Create Account
            </Button>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-heading font-bold mb-2">Welcome Back</h1>
              <p className="text-muted-foreground">Secure access to your dashboard</p>
            </div>

            {/* Login Form Card */}
            <Card className="border-border/50 shadow-xl shadow-primary/5">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="email" 
                      className={`text-sm transition-colors ${focusedField === 'email' ? 'text-primary' : ''}`}
                    >
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="name@company.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (errors.email) setErrors({ ...errors, email: null });
                        }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 h-12 rounded-sm transition-all ${
                          errors.email ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'email' ? 'border-primary' : ''
                        }`}
                        data-testid="login-email-input"
                      />
                      {formData.email && !errors.email && (
                        <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                      )}
                    </div>
                    {errors.email && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label 
                        htmlFor="password"
                        className={`text-sm transition-colors ${focusedField === 'password' ? 'text-primary' : ''}`}
                      >
                        Password
                      </Label>
                      <Link 
                        to="/forgot-password" 
                        className="text-xs text-primary hover:underline"
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({ ...formData, password: e.target.value });
                          if (errors.password) setErrors({ ...errors, password: null });
                        }}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 pr-10 h-12 rounded-sm transition-all ${
                          errors.password ? 'border-red-500 focus-visible:ring-red-500' : 
                          focusedField === 'password' ? 'border-primary' : ''
                        }`}
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                        data-testid="toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 rounded-full bg-red-500" />
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Submit Error */}
                  {errors.submit && (
                    <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-sm text-red-500">
                      {errors.submit}
                    </div>
                  )}

                  {/* Login Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-sm glow-primary text-base font-semibold" 
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                    ) : (
                      <>Sign In <ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                    </div>
                  </div>

                  {/* OTP Login */}
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full h-11 rounded-sm"
                    onClick={() => toast.info('OTP login coming soon')}
                    data-testid="otp-login-btn"
                  >
                    <Smartphone className="mr-2 h-4 w-4" />
                    Login with OTP
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Bottom Link */}
            <p className="text-center text-sm text-muted-foreground mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="signup-link">
                Create Account
              </Link>
            </p>
            <p className="text-center text-xs text-muted-foreground mt-3">
              Administrator?{' '}
              <Link to="/admin/login" className="text-primary font-medium hover:underline" data-testid="admin-login-link">
                Admin Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
